import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid2';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { PhoneInput } from 'react-international-phone';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import 'react-international-phone/style.css';
import apiClient from '../../../../utils/apiClient';
import decodeToken from '../../../../utils/decode-token';
import { applyLanguage } from '../../../../i18n';
import SettingsPage from './settings-page';
import SettingsSection from './settings-section';
import PasswordSettingsSection from './password-settings-section';
import useAvailableLanguages from './use-available-languages';
import { getTranslationUserEndpoint } from '../../../../api/translationUsersApi';
import LanguageSettingsSection from './language-settings-section';

const ROLE_SETTINGS = {
  admin: {
    roles: ['admin', 'marketAdmin', 'countryAdmin'],
    backTo: '/admin/main/',
    getProfile: ({ id }) => `/admin/${id}`,
    saveProfile: () => '/admin/personal-settings',
    areaCode: (profile) => profile.area_code,
    phone: true,
    reload: true,
  },
  shippingAdmin: {
    roles: ['shippingAdmin'],
    backTo: '/shipping-admin/main/',
    getProfile: ({ id }) => `/admin/${id}`,
    saveProfile: () => '/admin/personal-settings',
    areaCode: (profile) => profile.area_code,
    phone: true,
    reload: true,
  },
  retailer: {
    roles: ['retailer', 'retailerGM'],
    backTo: '/retailer/main/',
    getProfile: ({ id }) => `/retailer/staff-member?staffId=${id}`,
    saveProfile: ({ id }) => `/retailer/retailer-staff/personal-settings?staffId=${id}`,
    areaCode: (profile) => profile.retailerInfo?.[0]?.area_code,
    phone: true,
    reload: true,
  },
  translator: {
    roles: ['translator', 'reviewer'],
    backTo: '/admin/main/library',
    getProfile: ({ id, role }) => (
      getTranslationUserEndpoint(role, id)
    ),
    saveProfile: ({ id, role }) => (
      getTranslationUserEndpoint(role, id)
    ),
    email: true,
  },
};

const PersonalSettings = ({ variant }) => {
  const { t } = useTranslation();
  const token = sessionStorage.getItem('token');
  const identity = token ? decodeToken(token) : {};
  const config = ROLE_SETTINGS[variant];
  const context = { id: identity.id, role: identity.role };
  const allowed = Boolean(token && config?.roles.includes(identity.role));
  const { register, handleSubmit, reset, setFocus, formState: { errors } } = useForm();
  const [profile, setProfile] = useState(null);
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [language, setLanguage] = useState('');
  const [feedback, setFeedback] = useState({ open: false, severity: 'success', message: '' });
  const areaCode = profile ? config?.areaCode?.(profile) || profile.area_code : null;
  const { languages, error: languagesError } = useAvailableLanguages({
    areaCodes: areaCode ? [areaCode] : [],
    assignments: variant === 'translator' ? profile?.languages : undefined,
    enabled: allowed && profile !== null && config?.showLanguageSettings !== false,
  });

  const showFeedback = (severity, message) => setFeedback({ open: true, severity, message });

  useEffect(() => {
    if (!languagesError) return;
    setFeedback({
      open: true,
      severity: 'error',
      message: t('common-internal-server-error'),
    });
  }, [languagesError, t]);

  useEffect(() => {
    if (!allowed) return;
    apiClient.get(config.getProfile(context))
      .then(({ data }) => {
        setProfile(data);
        setLanguage(data.preferred_language || '');
        setPhone(`${data.prefix_phone || ''}${data.phone || ''}`);
        reset({
          name: data.name || '',
          lastName: data.last_name || '',
          email: data.email || '',
        });
      })
      .catch(() => showFeedback('error', t('common-internal-server-error')));
    // context is derived from the token and remains stable for this mounted page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, config, reset, t]);

  const persistLocalProfile = (updates) => {
    const updated = { ...profile, ...updates };
    setProfile(updated);
    sessionStorage.setItem('loggedUser', JSON.stringify(updated));
    window.dispatchEvent(new CustomEvent('hac:logged-user-updated', { detail: updated }));
  };

  const afterSave = (updates, message) => {
    persistLocalProfile(updates);
    showFeedback('success', message);
    if (config.reload) {
      setTimeout(() => window.location.reload(), 2000);
    }
  };

  const buildTranslationProfilePayload = (updates) => ({
    name: updates.name ?? profile.name,
    last_name: updates.last_name ?? profile.last_name,
    email: profile.email,
    area_code: profile.area_code,
    preferred_language: updates.preferred_language ?? profile.preferred_language,
    languages: profile.languages,
  });

  const saveContact = async ({ name, lastName }) => {
    let contact = { name, last_name: lastName };
    if (config.phone) {
      const parsedPhone = parsePhoneNumberFromString(phone);
      if (!parsedPhone?.isValid()) {
        setPhoneError(t('alert-phone-format'));
        setFocus('phone');
        showFeedback('error', t('alert-phone-format'));
        return;
      }
      contact = {
        ...contact,
        prefix_phone: `+${parsedPhone.countryCallingCode}`,
        phone: parsedPhone.nationalNumber,
      };
    }

    try {
      const payload = variant === 'translator'
        ? buildTranslationProfilePayload(contact)
        : contact;
      if (variant === 'translator') {
        await apiClient.put(config.saveProfile(context), payload);
      } else {
        await apiClient.patch(config.saveProfile(context), payload);
      }
      afterSave(contact, t('personal-settings-save-success'));
    } catch {
      showFeedback('error', t('common-internal-server-error'));
    }
  };

  const saveLanguage = async () => {
    try {
      if (variant === 'translator') {
        await apiClient.put(
          config.saveProfile(context),
          buildTranslationProfilePayload({ preferred_language: language }),
        );
      } else {
        await apiClient.patch(config.saveProfile(context), { language });
      }
      await applyLanguage(language);
      afterSave({ preferred_language: language }, t('language-settings-save-success'));
    } catch {
      showFeedback('error', t('common-internal-server-error'));
    }
  };

  return (
    <SettingsPage
      allowed={allowed}
      backTo={config?.backTo}
      feedback={feedback}
      onCloseFeedback={() => setFeedback((current) => ({ ...current, open: false }))}
    >
      <SettingsSection title={t('personal-settings-title')} defaultExpanded={variant === 'translator'}>
        <form onSubmit={handleSubmit(saveContact)}>
          <Grid container spacing={1}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <div className={`form-control required ${errors.name ? 'error' : ''}`}>
                <label htmlFor="name">{t('common-first-name')}</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  maxLength="50"
                  autoComplete="off"
                  {...register('name', { required: t('alert-name') })}
                />
                {errors.name && <span>{errors.name.message}</span>}
              </div>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <div className={`form-control required ${errors.lastName ? 'error' : ''}`}>
                <label htmlFor="lastName">{t('common-last-name')}</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  maxLength="50"
                  autoComplete="off"
                  {...register('lastName', { required: t('alert-last-name') })}
                />
                {errors.lastName && <span>{errors.lastName.message}</span>}
              </div>
            </Grid>
            {config.email && (
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <div className="form-control">
                  <label htmlFor="email">{t('common-email')}</label>
                  <input id="email" name="email" type="email" disabled {...register('email')} />
                </div>
              </Grid>
            )}
          </Grid>
          {config.phone && (
            <Grid container spacing={1}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <div className={`form-control required ${phoneError ? 'error' : ''}`}>
                  <label htmlFor="phone">{t('common-phone')}</label>
                  <PhoneInput
                    value={phone}
                    onChange={(value) => {
                      setPhone(value);
                      setPhoneError('');
                    }}
                  />
                  {phoneError && <span>{phoneError}</span>}
                </div>
              </Grid>
            </Grid>
          )}
          <div className="actions">
            <button className="btn" type="submit">{t('common-save-changes')}</button>
          </div>
        </form>
      </SettingsSection>

      {config.showLanguageSettings !== false && (
        <LanguageSettingsSection
          languages={languages}
          selectedLanguage={language}
          onLanguageChange={setLanguage}
          onSave={saveLanguage}
        />
      )}

      <PasswordSettingsSection />
    </SettingsPage>
  );
};

export default PersonalSettings;
